// src/components/profile/ProfileSettings.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  PhotoCamera as CameraIcon,
  Link as LinkIcon,
  SportsEsports as GameIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  PersonOutline as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import useProfileActions from '../../hooks/useProfileActions';
import { apiService } from '../../services/api';
import { resolveMediaUrl } from '../../utils/mediaUrl';

function ProfileSettings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { setUser: setAuthUser } = useAuth();
  const { user, uploadAvatar, updateProfile, addGameAccount, removeGameAccount, loading: profileLoading } = useProfileActions();
  
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [socialLinks, setSocialLinks] = useState({
    twitch: '',
    twitter: '',
    discord: '',
  });
  const [profileData, setProfileData] = useState({
    bio: user?.bio || '',
    username: user?.username || '',
  });
  const [gameAccountDialog, setGameAccountDialog] = useState(false);
  const [newGameAccount, setNewGameAccount] = useState({
    gameName: '',
    accountId: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [emailData, setEmailData] = useState({
    newEmail: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setProfileData({
        bio: user.bio || '',
        username: user.username || '',
      });
      // Initialize social links if they exist
      if (user.social_links && user.social_links.length > 0) {
        const socialLinksMap = {};
        user.social_links.forEach(link => {
          socialLinksMap[link.platform] = link.url;
        });
        setSocialLinks({
          twitch: socialLinksMap.twitch || '',
          twitter: socialLinksMap.twitter || '',
          discord: socialLinksMap.discord || '',
        });
      }
    }
  }, [user]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type and size
      if (!selectedFile.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024) { // 5MB limit
        setError('File size must be less than 5MB');
        return;
      }
      
      setFile(selectedFile);
      setError('');
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSocialChange = (e) => {
    setSocialLinks({ 
      ...socialLinks, 
      [e.target.name]: e.target.value 
    });
  };

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handleGameAccountChange = (e) => {
    setNewGameAccount({
      ...newGameAccount,
      [e.target.name]: e.target.value
    });
  };

  const handleAvatarSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await uploadAvatar(file);
      setSuccess('Avatar updated successfully!');
      setFile(null);
      setPreviewUrl(null);
    } catch (err) {
      setError('Failed to upload avatar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepare social links data
      const updatedLinks = Object.entries(socialLinks)
        .filter(([_, url]) => url.trim() !== '')
        .map(([platform, url]) => ({ platform, url }));

      const updateData = {
        ...profileData,
        ...(updatedLinks.length > 0 && { social_links: updatedLinks })
      };

      await updateProfile(updateData);
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGameAccount = async () => {
    if (!newGameAccount.gameName.trim() || !newGameAccount.accountId.trim()) {
      setError('Please fill in both game name and account ID');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const success = await addGameAccount(newGameAccount);
      if (success) {
        setSuccess('Game account added successfully!');
        setGameAccountDialog(false);
        setNewGameAccount({ gameName: '', accountId: '' });
      } else {
        setError('Failed to add game account. Please try again.');
      }
    } catch (err) {
      setError('Failed to add game account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGameAccount = async (index) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await removeGameAccount(index);
      setSuccess('Game account removed successfully!');
    } catch (err) {
      setError('Failed to remove game account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeGameAccountDialog = () => {
    setGameAccountDialog(false);
    setNewGameAccount({ gameName: '', accountId: '' });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleEmailChange = (e) => {
    setEmailData({
      ...emailData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!passwordData.currentPassword) {
      setError('Current password is required');
      setLoading(false);
      return;
    }

    if (!passwordData.newPassword) {
      setError('New password is required');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const result = await apiService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      if (result.success) {
        setSuccess('Password changed successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setError(result.error || 'Failed to change password');
      }
    } catch (err) {
      setError('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!emailData.newEmail) {
      setError('New email is required');
      setLoading(false);
      return;
    }

    if (!emailData.password) {
      setError('Password is required to change email');
      setLoading(false);
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailData.newEmail)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const result = await apiService.changeEmail(
        emailData.newEmail,
        emailData.password
      );

      if (result.success) {
        setSuccess('Email changed successfully! Please use your new email to log in next time.');
        setEmailData({
          newEmail: '',
          password: ''
        });
        
        // Update the auth context with the new user data
        if (setAuthUser && result.data) {
          setAuthUser(result.data);
        }
      } else {
        setError(result.error || 'Failed to change email');
      }
    } catch (err) {
      setError('Failed to change email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress 
          size={60}
          sx={{
            color: '#C9A227',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#14110E',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 4, sm: 6, md: 8 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 4, sm: 6 }, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha('#C9A227', 0.1),
              border: '1px solid rgba(201, 162, 39, 0.2)',
            }}
          >
            <SettingsIcon 
              sx={{ 
                fontSize: { xs: 32, sm: 40 },
                color: '#C9A227',
              }} 
            />
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                color: '#F3EDE2', fontFamily: '"Fraunces", serif',
                letterSpacing: '-0.02em',
              }}
            >
              Profile Settings
            </Typography>
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              color: alpha('#fff', 0.7),
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
            }}
          >
            Customize your profile and manage your account
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              bgcolor: alpha('#EF4444', 0.1),
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#FCA5A5',
              '& .MuiAlert-icon': {
                color: '#EF4444'
              }
            }} 
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              bgcolor: alpha('#6B8F71', 0.1),
              border: '1px solid rgba(107, 143, 113, 0.3)',
              color: '#6EE7B7',
              '& .MuiAlert-icon': {
                color: '#6B8F71'
              }
            }} 
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Left Column - Avatar & Quick Info */}
          <Grid item xs={12} md={4}>
            {/* Avatar Upload Section */}
            <Card 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                bgcolor: alpha('#241E18', 0.6),
                border: '1px solid rgba(201, 162, 39, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: alpha('#C9A227', 0.4),
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    color: '#3D7A8C',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  <CameraIcon sx={{ mr: 1, color: '#C9A227' }} />
                  Profile Picture
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    src={(() => {
                      const avatarUrl = previewUrl || resolveMediaUrl(user?.avatar);
                      return avatarUrl;
                    })()}
                    sx={{ 
                      width: { xs: 120, sm: 150 },
                      height: { xs: 120, sm: 150 },
                      border: '3px solid',
                      borderColor: '#C9A227',
                      fontSize: { xs: '2.5rem', sm: '3rem' },
                      fontWeight: 700,
                      bgcolor: alpha('#C9A227', 0.2),
                    }}
                  >
                    {user?.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  
                  <Box sx={{ width: '100%', textAlign: 'center' }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="avatar-upload"
                      type="file"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="avatar-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        fullWidth={isMobile}
                        startIcon={<UploadIcon />}
                        sx={{ 
                          mb: 1,
                          borderColor: '#3D7A8C',
                          color: '#3D7A8C',
                          '&:hover': {
                            borderColor: '#2A5563',
                            bgcolor: alpha('#3D7A8C', 0.1),
                          }
                        }}
                      >
                        Choose Image
                      </Button>
                    </label>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.6), fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      JPG, PNG up to 5MB
                    </Typography>
                  </Box>
                </Box>

                {file && (
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <Button
                      onClick={handleAvatarSubmit}
                      variant="contained"
                      fullWidth={isMobile}
                      startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                      disabled={loading}
                      sx={{
                        backgroundColor: '#E85D4C', color: '#14110E',
                        '&:hover': {
                          backgroundColor: '#C44738',
                        }
                      }}
                    >
                      {loading ? 'Uploading...' : 'Upload'}
                    </Button>
                    <Button
                      onClick={() => {
                        setFile(null);
                        setPreviewUrl(null);
                      }}
                      variant="outlined"
                      fullWidth={isMobile}
                      disabled={loading}
                      sx={{
                        borderColor: alpha('#fff', 0.2),
                        color: alpha('#fff', 0.7),
                        '&:hover': {
                          borderColor: alpha('#fff', 0.4),
                          bgcolor: alpha('#fff', 0.05),
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* User Info Card */}
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: alpha('#241E18', 0.6),
                border: '1px solid rgba(61, 122, 140, 0.2)',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: '#3D7A8C', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Account Info
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ color: '#C9A227', fontSize: { xs: 20, sm: 24 } }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      Username
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {user?.username}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ color: '#3D7A8C', fontSize: { xs: 20, sm: 24 } }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {user?.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column - Settings Forms */}
          <Grid item xs={12} md={8}>
            {/* Profile Information Section */}
            <Card 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                bgcolor: alpha('#241E18', 0.6),
                border: '1px solid rgba(201, 162, 39, 0.2)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    color: '#3D7A8C',
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: '1.125rem', sm: '1.5rem' },
                  }}
                >
                  Profile Information
                </Typography>
                
                <Box component="form" onSubmit={handleProfileSubmit}>
                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Username"
                        name="username"
                        value={profileData.username}
                        onChange={handleProfileChange}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#C9A227', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#C9A227', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#C9A227',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#C9A227',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Bio"
                        name="bio"
                        value={profileData.bio}
                        onChange={handleProfileChange}
                        multiline
                        rows={4}
                        variant="outlined"
                        placeholder="Tell us about yourself..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#C9A227', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#C9A227', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#C9A227',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#C9A227',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3, borderColor: alpha('#fff', 0.1) }} />

                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: '#3D7A8C',
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                    }}
                  >
                    <LinkIcon sx={{ mr: 1, color: '#C9A227' }} />
                    Social Links
                  </Typography>

                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Twitch URL"
                        name="twitch"
                        value={socialLinks.twitch}
                        onChange={handleSocialChange}
                        variant="outlined"
                        placeholder="https://twitch.tv/username"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#3D7A8C', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#3D7A8C', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#3D7A8C',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#3D7A8C',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Twitter URL"
                        name="twitter"
                        value={socialLinks.twitter}
                        onChange={handleSocialChange}
                        variant="outlined"
                        placeholder="https://twitter.com/username"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#3D7A8C', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#3D7A8C', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#3D7A8C',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#3D7A8C',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Discord Invite URL"
                        name="discord"
                        value={socialLinks.discord}
                        onChange={handleSocialChange}
                        variant="outlined"
                        placeholder="https://discord.gg/invite"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#3D7A8C', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#3D7A8C', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#3D7A8C',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#3D7A8C',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 4 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth={isMobile}
                      startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                      disabled={loading}
                      size="large"
                      sx={{
                        backgroundColor: '#E85D4C', color: '#14110E',
                        fontWeight: 700,
                        '&:hover': {
                          backgroundColor: '#C44738',
                        },
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth={isMobile}
                      onClick={() => {
                        setProfileData({
                          bio: user?.bio || '',
                          username: user?.username || '',
                        });
                        setSocialLinks({
                          twitch: '',
                          twitter: '',
                          discord: '',
                        });
                      }}
                      disabled={loading}
                      size="large"
                      sx={{
                        borderColor: alpha('#fff', 0.2),
                        color: alpha('#fff', 0.7),
                        '&:hover': {
                          borderColor: alpha('#fff', 0.4),
                          bgcolor: alpha('#fff', 0.05),
                        }
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Game Accounts Section */}
            <Card 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                bgcolor: alpha('#241E18', 0.6),
                border: '1px solid rgba(61, 122, 140, 0.2)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: '#3D7A8C',
                      fontWeight: 700,
                      fontSize: { xs: '1.125rem', sm: '1.5rem' },
                    }}
                  >
                    <GameIcon sx={{ mr: 1, color: '#C9A227' }} />
                    Game Accounts
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth={isMobile}
                    startIcon={<AddIcon />}
                    onClick={() => setGameAccountDialog(true)}
                    disabled={loading}
                    sx={{
                      backgroundColor: '#E85D4C', color: '#14110E',
                      '&:hover': {
                        backgroundColor: '#C44738',
                      }
                    }}
                  >
                    Add Account
                  </Button>
                </Box>

                {user?.game_accounts && user.game_accounts.length > 0 ? (
                  <List sx={{ bgcolor: alpha('#14110E', 0.3), borderRadius: 2 }}>
                    {user.game_accounts.map((account, index) => (
                      <ListItem 
                        key={index} 
                        divider={index < user.game_accounts.length - 1}
                        sx={{
                          py: 2,
                          '&:hover': {
                            bgcolor: alpha('#C9A227', 0.05),
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip 
                                label={account.game_name} 
                                sx={{
                                  bgcolor: alpha('#3D7A8C', 0.2),
                                  color: '#6AA3B3',
                                  borderColor: alpha('#3D7A8C', 0.3),
                                  border: '1px solid',
                                  fontWeight: 700,
                                }}
                                size="small" 
                                variant="outlined"
                              />
                              <Typography variant="body1" sx={{ fontWeight: 500, color: 'white', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {account.account_id}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ color: alpha('#fff', 0.6), fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              Game: {account.game_name}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleRemoveGameAccount(index)}
                            disabled={loading}
                            sx={{
                              color: '#EF4444',
                              '&:hover': {
                                bgcolor: alpha('#EF4444', 0.1),
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box 
                    sx={{ 
                      textAlign: 'center', 
                      py: { xs: 4, sm: 6 },
                      bgcolor: alpha('#14110E', 0.3),
                      borderRadius: 2,
                      border: `2px dashed ${alpha('#3D7A8C', 0.3)}`,
                    }}
                  >
                    <GameIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: alpha('#3D7A8C', 0.5), mb: 2 }} />
                    <Typography variant="body1" sx={{ color: alpha('#fff', 0.7), mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      No game accounts added yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.5), fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Add your gaming accounts to connect with other players
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Email Change Section */}
            <Card 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                bgcolor: alpha('#241E18', 0.6),
                border: '1px solid rgba(61, 122, 140, 0.2)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    color: '#3D7A8C',
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: '1.125rem', sm: '1.5rem' },
                  }}
                >
                  <EmailIcon sx={{ mr: 1, color: '#C9A227' }} />
                  Change Email
                </Typography>
                
                <Box component="form" onSubmit={handleEmailSubmit}>
                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="New Email Address"
                        name="newEmail"
                        type="email"
                        value={emailData.newEmail}
                        onChange={handleEmailChange}
                        variant="outlined"
                        placeholder="your.new.email@example.com"
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#3D7A8C', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#3D7A8C', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#3D7A8C',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#3D7A8C',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Password (for verification)"
                        name="password"
                        type="password"
                        value={emailData.password}
                        onChange={handleEmailChange}
                        variant="outlined"
                        helperText="Enter your current password to confirm this change"
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#3D7A8C', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#3D7A8C', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#3D7A8C',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#3D7A8C',
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            color: alpha('#fff', 0.5),
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth={isMobile}
                      color="info"
                      startIcon={loading ? <CircularProgress size={16} /> : <EmailIcon />}
                      disabled={loading || !emailData.newEmail || !emailData.password}
                      size="large"
                      sx={{
                        backgroundColor: '#E85D4C', color: '#14110E',
                        fontWeight: 700,
                        '&:hover': {
                          backgroundColor: '#C44738',
                        },
                      }}
                    >
                      {loading ? 'Changing Email...' : 'Change Email'}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth={isMobile}
                      onClick={() => {
                        setEmailData({
                          newEmail: '',
                          password: ''
                        });
                      }}
                      disabled={loading}
                      size="large"
                      sx={{
                        borderColor: alpha('#fff', 0.2),
                        color: alpha('#fff', 0.7),
                        '&:hover': {
                          borderColor: alpha('#fff', 0.4),
                          bgcolor: alpha('#fff', 0.05),
                        }
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Password Change Section */}
            <Card 
              sx={{ 
                bgcolor: alpha('#241E18', 0.6),
                border: '1px solid rgba(239, 68, 68, 0.2)',
                boxShadow: '0 8px 32px rgba(239, 68, 68, 0.15)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    color: '#F87171',
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: '1.125rem', sm: '1.5rem' },
                  }}
                >
                  <LockIcon sx={{ mr: 1, color: '#EF4444' }} />
                  Change Password
                </Typography>
                
                <Box component="form" onSubmit={handlePasswordSubmit}>
                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Current Password"
                        name="currentPassword"
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        variant="outlined"
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#EF4444', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#EF4444', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#EF4444',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#F87171',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="New Password"
                        name="newPassword"
                        type="password"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        variant="outlined"
                        placeholder="Minimum 6 characters"
                        helperText="Password must be at least 6 characters long"
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#EF4444', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#EF4444', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#EF4444',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#F87171',
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            color: alpha('#fff', 0.5),
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Confirm New Password"
                        name="confirmPassword"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        variant="outlined"
                        error={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword}
                        helperText={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword ? "Passwords do not match" : ""}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            bgcolor: alpha('#14110E', 0.5),
                            '& fieldset': {
                              borderColor: alpha('#EF4444', 0.3),
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#EF4444', 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#EF4444',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: alpha('#fff', 0.7),
                            '&.Mui-focused': {
                              color: '#F87171',
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#EF4444',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth={isMobile}
                      color="warning"
                      startIcon={loading ? <CircularProgress size={16} /> : <LockIcon />}
                      disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword}
                      size="large"
                      sx={{
                        backgroundColor: '#A33B32', color: '#F3EDE2',
                        boxShadow: '0 4px 20px rgba(239, 68, 68, 0.3)',
                        fontWeight: 700,
                        '&:hover': {
                          backgroundColor: '#7A2B24',
                          boxShadow: '0 6px 25px rgba(239, 68, 68, 0.4)',
                        },
                      }}
                    >
                      {loading ? 'Changing Password...' : 'Change Password'}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth={isMobile}
                      onClick={() => {
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                      disabled={loading}
                      size="large"
                      sx={{
                        borderColor: alpha('#fff', 0.2),
                        color: alpha('#fff', 0.7),
                        '&:hover': {
                          borderColor: alpha('#fff', 0.4),
                          bgcolor: alpha('#fff', 0.05),
                        }
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Add Game Account Dialog */}
      <Dialog 
        open={gameAccountDialog} 
        onClose={closeGameAccountDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: alpha('#241E18', 0.95),
            border: '1px solid rgba(201, 162, 39, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            color: 'white',
            borderBottom: `1px solid ${alpha('#fff', 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GameIcon sx={{ color: '#C9A227' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
              Add Game Account
            </Typography>
          </Box>
          <IconButton 
            onClick={closeGameAccountDialog}
            sx={{ 
              color: alpha('#fff', 0.7),
              '&:hover': {
                bgcolor: alpha('#fff', 0.1),
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Game Name"
                name="gameName"
                value={newGameAccount.gameName}
                onChange={handleGameAccountChange}
                variant="outlined"
                placeholder="e.g., League of Legends, Valorant, CS:GO"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    bgcolor: alpha('#14110E', 0.5),
                    '& fieldset': {
                      borderColor: alpha('#C9A227', 0.3),
                    },
                    '&:hover fieldset': {
                      borderColor: alpha('#C9A227', 0.5),
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#C9A227',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: alpha('#fff', 0.7),
                    '&.Mui-focused': {
                      color: '#C9A227',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account ID / Username"
                name="accountId"
                value={newGameAccount.accountId}
                onChange={handleGameAccountChange}
                variant="outlined"
                placeholder="Your in-game username or ID"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    bgcolor: alpha('#14110E', 0.5),
                    '& fieldset': {
                      borderColor: alpha('#C9A227', 0.3),
                    },
                    '&:hover fieldset': {
                      borderColor: alpha('#C9A227', 0.5),
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#C9A227',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: alpha('#fff', 0.7),
                    '&.Mui-focused': {
                      color: '#C9A227',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha('#fff', 0.1)}` }}>
          <Button 
            onClick={closeGameAccountDialog} 
            disabled={loading}
            sx={{
              color: alpha('#fff', 0.7),
              '&:hover': {
                bgcolor: alpha('#fff', 0.05),
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddGameAccount}
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
            disabled={loading || !newGameAccount.gameName.trim() || !newGameAccount.accountId.trim()}
            sx={{
              backgroundColor: '#E85D4C', color: '#14110E',
              '&:hover': {
                backgroundColor: '#C44738',
              }
            }}
          >
            {loading ? 'Adding...' : 'Add Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProfileSettings;
